import { DestinationFormData } from '@/types/destinationFormData'
import { css, Theme } from '@emotion/react'
import { PrefectureSelect } from '@/components/select/PrefectureSelect'
import { BaseInput } from '@/components/input/BaseInput'
import { FormSection } from '@/components/form/FormSection'
import { FormFieldRow } from '@/components/form/FormFieldRow'
import { FormButtonArea } from '@/components/form/FormButtonArea'
import { useAtom } from 'jotai'
import { isDisabledAtom } from '@/store/state'

// 白い枠のコンテナ
const whiteContainerStyle = css`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 32px;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
  position: relative;
`

// 必須注記（右上）
const requiredNoteStyle = (t: Theme) => css`
  position: absolute;
  top: 32px;
  right: 32px;
  font-size: 1.4rem;
  color: ${t.c.ec.black};
`

// 必須マーク（赤色）
const requiredMarkStyle = css`
  color: #dc3545;
`

// フォームスタイル
const formStyle = css`
  display: flex;
  flex-direction: column;
`

// 適用開始日エリア（32px余白）
const startDateAreaStyle = css`
  margin-bottom: 32px;
`

// 郵便番号の横並びレイアウト
const postalCodeStyle = css`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`

// フィールドサイズ定義
const fieldSizes = {
  startDate: css`
    width: 150px;
    height: 26px;
    font-size: 14px;
  `,
  destinationCode: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  name: css`
    width: 400px;
    height: 26px;
    font-size: 14px;
  `,
  postalCode1: css`
    width: 80px;
    height: 26px;
    font-size: 14px;
  `,
  postalCode2: css`
    width: 100px;
    height: 26px;
    font-size: 14px;
  `,
  prefecture: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  addressLine1: css`
    width: 500px;
    height: 26px;
    font-size: 14px;
  `,
  addressLine2: css`
    width: 500px;
    height: 26px;
    font-size: 14px;
  `,
  phoneNumber: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  faxNumber: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  destinationsPhoneNumber: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
}

// 都道府県セレクトボックスのカスタムスタイル（26px高さ）
const prefectureSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    height: '26px',
    minHeight: '26px',
    fontSize: '14px',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: '26px',
    padding: '0 8px',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: '0',
    padding: '0',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '26px',
  }),
}

type Props = {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectChange: (newValue: { value: string; label: string } | null) => void
  onCancel: () => void
  formData: DestinationFormData
  errors: { [key: string]: string }
  id?: string
}

export const DestinationForm = ({
  onSubmit,
  onChange,
  onSelectChange,
  onCancel,
  formData,
  errors,
  id,
}: Props) => {
  const [isDisabled] = useAtom(isDisabledAtom)

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const formEvent = new Event('submit', { bubbles: true, cancelable: true })
    onSubmit(formEvent as any)
  }

  return (
    <>
      {/* 白い枠のコンテナ */}
      <div css={whiteContainerStyle}>
        {/* 必須注記（右上） */}
        <div css={requiredNoteStyle}>
          <span css={requiredMarkStyle}>*</span>は必須項目です。
        </div>

        <form css={formStyle}>
          {/* 適用開始日（独立） */}
          <div css={startDateAreaStyle}>
            <FormFieldRow label="適用開始日" required error={errors.startDate}>
              <BaseInput
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={onChange}
                addStyle={fieldSizes.startDate}
                min={new Date().toISOString().split('T')[0]}
                isError={!!errors.startDate}
              />
            </FormFieldRow>
          </div>

          {/* 基本情報セクション */}
          <FormSection title="1 基本情報" isFirst>
            <FormFieldRow
              label="送達先番号"
              required
              error={errors.destinationCode}
            >
              <BaseInput
                type="text"
                name="destinationCode"
                maxLength={8}
                value={formData.destinationCode}
                onChange={onChange}
                addStyle={fieldSizes.destinationCode}
                isError={!!errors.destinationCode}
              />
            </FormFieldRow>

            <FormFieldRow label="送達先名" required error={errors.name}>
              <BaseInput
                type="text"
                name="name"
                maxLength={50}
                value={formData.name}
                onChange={onChange}
                addStyle={fieldSizes.name}
                isError={!!errors.name}
              />
            </FormFieldRow>

            <FormFieldRow
              label="都道府県"
              required
              error={errors.prefectureCode}
            >
              <PrefectureSelect
                initialValue={formData.prefectureCode}
                isSearchable={false}
                onChange={onSelectChange}
                addStyle={fieldSizes.prefecture}
                customStyles={prefectureSelectStyles}
                isError={!!errors.prefectureCode}
              />
            </FormFieldRow>
          </FormSection>

          {/* 住所・連絡先セクション */}
          <FormSection title="2 住所・連絡先" showDivider>
            <FormFieldRow label="郵便番号" required error={errors.postalCode}>
              <div css={postalCodeStyle}>
                <BaseInput
                  type="text"
                  name="postalCode1"
                  maxLength={3}
                  value={formData.postalCode1}
                  onChange={onChange}
                  addStyle={fieldSizes.postalCode1}
                  isError={!!errors.postalCode}
                />
                <span>-</span>
                <BaseInput
                  type="text"
                  name="postalCode2"
                  maxLength={4}
                  value={formData.postalCode2}
                  onChange={onChange}
                  addStyle={fieldSizes.postalCode2}
                  isError={!!errors.postalCode}
                />
              </div>
            </FormFieldRow>

            <FormFieldRow
              label="住所１"
              required
              error={errors.addressLine1}
            >
              <BaseInput
                type="text"
                name="addressLine1"
                placeholder="英字、数字は半角で入力してください"
                value={formData.addressLine1}
                maxLength={50}
                onChange={onChange}
                addStyle={fieldSizes.addressLine1}
                isError={!!errors.addressLine1}
              />
            </FormFieldRow>

            <FormFieldRow
              label="住所２"
              error={errors.addressLine2}
              helpText={
                '番地・住所以降' +
                '\n' +
                '階数表記はFではなく、階で入力してください'
              }
            >
              <BaseInput
                type="text"
                name="addressLine2"
                placeholder="英字、数字は半角で入力してください"
                value={formData.addressLine2}
                maxLength={50}
                onChange={onChange}
                addStyle={fieldSizes.addressLine2}
                isError={!!errors.addressLine2}
              />
            </FormFieldRow>

            <FormFieldRow label="電話番号" error={errors.phoneNumber}>
              <BaseInput
                type="text"
                name="phoneNumber"
                placeholder="例: 03-1234-5678"
                value={formData.phoneNumber}
                maxLength={20}
                onChange={onChange}
                addStyle={fieldSizes.phoneNumber}
                isError={!!errors.phoneNumber}
              />
            </FormFieldRow>

            <FormFieldRow label="FAX番号" error={errors.faxNumber}>
              <BaseInput
                type="text"
                name="faxNumber"
                placeholder="例: 03-1234-5678"
                value={formData.faxNumber}
                maxLength={20}
                onChange={onChange}
                addStyle={fieldSizes.faxNumber}
                isError={!!errors.faxNumber}
              />
            </FormFieldRow>

            <FormFieldRow
              label="送達先電話番号"
              error={errors.destinationsPhoneNumber}
            >
              <BaseInput
                type="text"
                name="destinationsPhoneNumber"
                placeholder="例: 03-1234-5678"
                value={formData.destinationsPhoneNumber}
                maxLength={20}
                onChange={onChange}
                addStyle={fieldSizes.destinationsPhoneNumber}
                isError={!!errors.destinationsPhoneNumber}
              />
            </FormFieldRow>
          </FormSection>
        </form>
      </div>

      {/* ボタンエリア */}
      <FormButtonArea
        cancelUrl="/destinations"
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={id ? '更新' : '登録'}
        isDisabled={isDisabled}
        showError={Object.values(errors).some((error) => error && error.trim() !== '')}
      />
    </>
  )
}
